import type { Document as RichTextDocument } from '@contentful/rich-text-types';
import Grid from '@mui/material/Grid';

import type { DialogContent } from '@/components/FormResultDialog/FormResultDialog';
import GridContainer from '@/components/Layout/GridContainer/GridContainer';
import { SpacingContainer } from '@/components/SpacingContainer/SpacingContainer';
import {
  FormOrientation,
  LeadForm,
} from '@/components/TopLevelComponents/LeadForm/LeadForm';
import type { GoogleUrls } from '@/constants/baseUrls';
import type { Contentful } from '@/contentful/contentful.types';
import type { LeadFormType } from '@/enums/FormType';
import useViewPortSize from '@/hooks/useViewportSize';
import type { ActionModalCF, LeadCreatedModalCF } from '@/types';
import { getLeadFormDialogContent } from '@/utils/leadForm';

import useStyles from './ActionUserInputHorizontal.styles';

export interface ActionUserInputHorizontalContent extends Contentful {
  fields: {
    heading?: string;
    termsOfService: RichTextDocument;
    submitButtonText?: string;
    requiresTcpaAcknowledgment?: boolean;
    isOnDark?: boolean;
    campaignId?: string;
    googleSheetUrls?: GoogleUrls;
    solarEdgeSubmissionUrl?: string;
    formType?: LeadFormType;
    dialogContent?: DialogContent;
    leadCreatedModal?: LeadCreatedModalCF | ActionModalCF;
    leadNotInServiceModal?: LeadCreatedModalCF | ActionModalCF;
    overrideCampaignId?: boolean;
    hideEmailInput?: boolean;
    hideHomeOwnershipQuestion?: boolean;
    redirectUrl?: string;
    outOfMarketRedirectUrl?: string;
    shouldRedirectWithParams?: boolean;
    useFullName?: boolean;
  };
}

interface ActionUserInputHorizontalProps {
  content: ActionUserInputHorizontalContent;
}

export default function ActionUserInputHorizontal({
  content: {
    fields: {
      heading,
      termsOfService,
      submitButtonText,
      requiresTcpaAcknowledgment = false,
      isOnDark,
      campaignId,
      googleSheetUrls,
      solarEdgeSubmissionUrl,
      formType,
      leadCreatedModal,
      leadNotInServiceModal,
      overrideCampaignId = false,
      hideEmailInput = false,
      hideHomeOwnershipQuestion = false,
      redirectUrl,
      outOfMarketRedirectUrl,
      shouldRedirectWithParams,
      useFullName = false,
    },
  },
}: ActionUserInputHorizontalProps) {
  const { classes, cx } = useStyles({ isOnDark });
  const { isSmall, isExtraSmall } = useViewPortSize();
  const isMdOrLarger = !isExtraSmall && !isSmall;

  const successDialogContent = getLeadFormDialogContent(
    leadCreatedModal,
    isMdOrLarger
  );
  const failDialogContent = getLeadFormDialogContent(
    leadNotInServiceModal,
    isMdOrLarger
  );

  return (
    <SpacingContainer className={cx(classes.container)}>
      <GridContainer gridProps={{ className: classes.wrapper }} fixed>
        <Grid size={12}>
          <LeadForm
            heading={heading}
            submitButtonText={submitButtonText}
            requiresTcpaAcknowledgment={requiresTcpaAcknowledgment}
            tcpa={termsOfService}
            orientation={FormOrientation.HORIZONTAL}
            isOnDark={isOnDark}
            campaignId={campaignId}
            googleSheetUrls={googleSheetUrls}
            solarEdgeSubmissionUrl={solarEdgeSubmissionUrl}
            formType={formType}
            successDialogContent={successDialogContent}
            failDialogContent={failDialogContent}
            overrideCampaignId={overrideCampaignId}
            hideEmailInput={hideEmailInput}
            hideHomeOwnershipQuestion={hideHomeOwnershipQuestion}
            redirectUrl={redirectUrl}
            outOfMarketRedirectUrl={outOfMarketRedirectUrl}
            shouldRedirectWithParams={shouldRedirectWithParams}
            useFullName={useFullName}
          />
        </Grid>
      </GridContainer>
    </SpacingContainer>
  );
}
